import React, { FormEvent, useState } from 'react'
import { observer } from 'mobx-react-lite'
import clsx from 'clsx'

import {
  Box,
  FormHelperText,
  FormLabel,
  Grid,
  Link,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import logoMpCard from '../assets/logoMPCard.svg'

import { CircleCheckbox } from './CircleCheckbox'
import { Footer } from './Footer'
import { PhoneNumberInput } from './PhoneNumberInput'

import { useBoolState } from '../hooks/commonHooks'
import { useMessages } from '../hooks/snackbarHooks'
import { useLogic, useStore } from '../hooks/storeHook'

import { required } from '../common/objectUtils'
import { isAppTypeMpSklad } from '../common/isAppTypeMpSklad'
import { MoySkladAppState } from '../server/mpsklad_core/Models/MoySkladAppState'

export type OnboardingScreenProps = {
  onFinish: VoidFunction
}

const offerAgreementUrl = 'https://mpsklad.ru/assets/договор-оферта-mpsklad_new.docx'

const contentMaxWidth = 555

export const OnboardingScreen =
  observer(
    ({onFinish}: OnboardingScreenProps) => {
      const {showSuccess} = useMessages()
      const logic = useLogic()
      const user = required(useStore().user)

      const [isLoading, setIsLoading, setNotLoading] = useBoolState()
      const [name, setName] = useState(user.name ?? '')
      const [phoneNumber, setPhoneNumber] = useState(user.phoneNumber ?? '')
      const [isOfferAccepted, setIsOfferAccepted] = useState(false)

      const classes = useStyles()

      const finishOnboarding = async (saveData: boolean) => {
        setIsLoading()

        try {
          await logic.completeOnboarding(
            saveData
              ? {
                name: name.trim() || undefined,
                phoneNumber: phoneNumber.trim() || undefined
              }
              : {})

          if (!isAppTypeMpSklad()) {
            user.moySkladAppState = MoySkladAppState.Active
          }

          if (saveData) {
            showSuccess('Сохранено!')
          }

          onFinish()
        } catch (ex) {
          console.error('Failed to complete onboarding', ex)
        } finally {
          setNotLoading()
        }
      }

      const onSubmit = async (e: FormEvent) => {
        e.preventDefault()
        await finishOnboarding(true)
      }

      const onSkip = async () => {
        await finishOnboarding(false)
      }

      const onGetHelpClick = () => {
        window.open('https://t.me/mpsklad2023', '_blank', 'noopener,noreferrer')
      }

      return (
        <div className={classes.page}>
          <Box className={classes.logoBar}>
            <img src={logoMpCard} alt="MPSKLADPIM" className={classes.logo}/>
          </Box>

          <Box className={classes.main}>
            <Box className={classes.contentColumn}>
              <Typography className={classes.pageTitle}>
                Настройка приложения
              </Typography>

              <Box className={classes.helpBanner}>
                <Box className={classes.helpBannerText}>
                  <Typography className={classes.helpBannerTitle}>
                    Бесплатная помощь с настройкой
                  </Typography>
                  <Typography className={classes.helpBannerSubtitle}>
                    Поможем подключить площадки и настроить карточки
                  </Typography>
                </Box>

                <button
                  type="button"
                  className={classes.helpBannerButton}
                  onClick={onGetHelpClick}
                >
                  Получить помощь
                </button>
              </Box>

              <form onSubmit={onSubmit} className={classes.form}>
                <Grid container className={clsx(classes.formCard, 'default-border')} alignContent="center">
                  <Grid item xs={12}>
                    <Box className={classes.formCardInner}>
                      <Typography className={classes.formTitle}>
                        Ввод личных данных
                      </Typography>

                      <Grid container direction="column" spacing={3}>
                        <Grid item xs={12}>
                          <FormLabel component="legend" className={classes.label}>
                            <span className={classes.dash}>—</span>Имя
                          </FormLabel>

                          <TextField
                            fullWidth
                            variant="outlined"
                            id="name"
                            autoComplete="fname"
                            placeholder="Введите имя"
                            InputLabelProps={{shrink: true}}
                            value={name}
                            onChange={e => setName(e.target.value)}
                            InputProps={{
                              className: classes.input
                            }}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <FormLabel component="legend" className={classes.label}>
                            <span className={classes.dash}>—</span>Телефон
                          </FormLabel>

                          <TextField
                            fullWidth
                            variant="outlined"
                            id="phone"
                            name="phone"
                            autoComplete="tel"
                            placeholder="+ 7 999 999 9999"
                            InputLabelProps={{shrink: true}}
                            InputProps={{
                              inputComponent: PhoneNumberInput,
                              inputProps: {onAccept: setPhoneNumber},
                              className: classes.input
                            }}
                          />

                          <FormHelperText className={classes.helperText}>
                            Поможем с настройкой, отправим инструкцию
                          </FormHelperText>
                        </Grid>
                      </Grid>

                      <Box marginTop={2}>
                        <CircleCheckbox
                          checked={isOfferAccepted}
                          onChange={e => setIsOfferAccepted(e.target.checked)}
                          label={
                            <Typography className={classes.offerText} component="span">
                              Используя сервис, вы соглашаетесь с условиями{' '}
                              <Link href={offerAgreementUrl} target="_blank" rel="noopener noreferrer">
                                договора оферты
                              </Link>
                            </Typography>
                          }
                        />
                      </Box>
                    </Box>
                  </Grid>
                </Grid>

                <Box className={classes.actions}>
                  <button
                    type="button"
                    className={clsx(classes.skipButton, 'default-button')}
                    disabled={isLoading}
                    onClick={onSkip}
                  >
                    <p>Пропустить</p>
                  </button>

                  <button
                    type="submit"
                    className={clsx(classes.nextButton, 'default-button')}
                    disabled={isLoading}
                  >
                    <p>{isLoading ? 'Сохраняем...' : 'Далее'}</p>
                  </button>
                </Box>
              </form>
            </Box>
          </Box>

          <Box className={classes.footerWrapper}>
            <Footer/>
          </Box>
        </div>
      )
    })

const useStyles = makeStyles(
  () => ({
    page: {
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#FFFFFF'
    },
    logoBar: {
      padding: '32px 48px 0'
    },
    logo: {
      height: 32,
      display: 'block'
    },
    main: {
      flex: 1,
      display: 'flex',
      justifyContent: 'center',
      padding: '48px 24px 32px'
    },
    contentColumn: {
      width: '100%',
      maxWidth: contentMaxWidth,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch'
    },
    pageTitle: {
      fontSize: 28,
      fontWeight: 700,
      lineHeight: 1.2,
      color: '#3987CF',
      textTransform: 'uppercase',
      marginBottom: 32,
      textAlign: 'center'
    },
    helpBanner: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 16,
      width: '100%',
      minHeight: 103,
      padding: '20px 24px',
      marginBottom: 24,
      borderRadius: 10,
      border: '1px solid #3987CF',
      backgroundColor: '#EEF4FD',
      boxSizing: 'border-box'
    },
    helpBannerText: {
      flex: 1,
      minWidth: 0
    },
    helpBannerTitle: {
      fontSize: 16,
      fontWeight: 700,
      lineHeight: 1.3,
      color: '#1F364D',
      marginBottom: 8
    },
    helpBannerSubtitle: {
      fontSize: 13.5,
      lineHeight: 1.4,
      color: '#6A7888'
    },
    helpBannerButton: {
      flexShrink: 0,
      minWidth: 160,
      height: 40,
      padding: '0 16px',
      borderRadius: 10,
      border: '1px solid #3987CF',
      backgroundColor: '#FFFFFF',
      color: '#2F6FD0',
      fontSize: 14,
      fontWeight: 500,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      '&:hover': {
        backgroundColor: '#F5FAFF'
      }
    },
    form: {
      width: '100%'
    },
    formCard: {
      width: '100%',
      borderRadius: 10,
      backgroundColor: '#FFFFFF'
    },
    formCardInner: {
      padding: 24,
      width: '100%',
      boxSizing: 'border-box'
    },
    formTitle: {
      fontSize: 18,
      textTransform: 'uppercase',
      fontWeight: 700,
      marginBottom: 24
    },
    label: {
      fontSize: 12,
      marginBottom: 12,
      display: 'block'
    },
    helperText: {
      fontSize: 11,
      fontWeight: 400,
      marginTop: 8,
      color: 'rgba(0, 0, 0, 0.54)'
    },
    input: {
      fontSize: 14,
      fontWeight: 400
    },
    dash: {
      color: '#3987CF',
      marginRight: 4
    },
    offerText: {
      fontSize: 12,
      fontWeight: 700,
      lineHeight: '14.4px'
    },
    actions: {
      display: 'flex',
      justifyContent: 'center',
      gap: 12,
      marginTop: 24,
      '& .default-button': {
        marginTop: 0,
        marginBottom: 0,
        maxWidth: 160,
        width: 160
      }
    },
    skipButton: {
      color: '#FFFFFF',
      backgroundColor: '#D2D2D2',
      '&:hover': {
        backgroundColor: '#BCBCBC'
      }
    },
    nextButton: {
      color: '#FFFFFF'
    },
    footerWrapper: {
      marginTop: 'auto',
      width: '100%'
    }
  }))
